import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { get } from 'lodash';
import { Button, Icon, Flyout } from '@ookla/common-ui';
import { formatSpeed } from './../../utils/format';
import c from './../../utils/constants';
import styles from './award.module.css';
import { Link } from 'gatsby';

export default class Award extends Component {
  static propTypes = {
    expandable: PropTypes.bool,
    awardColor: PropTypes.string,
    awardPageRoute: PropTypes.string,
    header: PropTypes.string.isRequired,
    subHeader: PropTypes.string.isRequired,
    companyBlurb: PropTypes.string.isRequired,
    tooltips: PropTypes.shape({
      speedScore: PropTypes.string
    }),
    className: PropTypes.string,
    report: PropTypes.shape({
      link: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    }),
    report2: PropTypes.shape({
      link: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    }),
    report3: PropTypes.shape({
      link: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired
    }),
    rankings: PropTypes.arrayOf(
      PropTypes.shape({
        companyName: PropTypes.string.isRequired,
        speed_score: PropTypes.string.isRequired,
        download_speed: PropTypes.string,
        upload_speed: PropTypes.string,
        coverage_score: PropTypes.string,
        video_score: PropTypes.string,
        game_score: PropTypes.string
      })
    ).isRequired
  };
  static defaultProps = {
    expandable: true
  };
  constructor(props) {
    super(props);
    this.state = {
      isExpanded: false
    };
  }
  toggleExpand = () => {
    this.setState((previousState) => {
      return {
        isExpanded: !previousState.isExpanded
      };
    });
  };
  isTableRowVisible = (rowIdx) => {
    if (!this.props.expandable || 0 === rowIdx) {
      return true;
    }

    return this.state.isExpanded;
  };
  renderRows = () => {
    const { rankings, awardColor } = this.props;
    if (!rankings) {
      return null;
    }

    // eslint-disable-next-line complexity
    return rankings.map((ranking, i) => {
      const rank = i + 1;
      const {
        speed_score,
        download_speed,
        upload_speed,
        coverage_score,
        rating,
        companyName,
        top_rated_name,
        carrier_name,
        video_score,
        game_score,
        overall_connectivity_score
      } = ranking;
      return (
        <tr key={rank} className={this.isTableRowVisible(i) ? undefined : styles.hidden}>
          <td className={styles.companyName}>
            <span style={awardColor && rank === 1 ? { color: awardColor } : {}}>{companyName || top_rated_name || carrier_name}</span>
          </td>
          {download_speed && !isNaN(download_speed) ? (
            <td className={styles.speedMeasure}>
              <span className={rank === 1 ? styles.colorDownload : undefined}>{formatSpeed(download_speed)}</span>
            </td>
          ) : null}
          {upload_speed && !isNaN(upload_speed) ? (
            <td className={styles.speedMeasure}>
              <span className={rank === 1 ? styles.colorUpload : undefined}>{formatSpeed(upload_speed)}</span>
            </td>
          ) : null}
          {rating && !isNaN(rating) ? (
            <td className={classNames(styles.speedMeasure, styles.speedScore)}>
              <span className={rank === 1 ? styles.colorTopRated : undefined}>{rating}</span>
            </td>
          ) : null}
          {coverage_score && !isNaN(coverage_score) ? (
            <td className={classNames(styles.speedMeasure, styles.speedScore)}>
              <span style={awardColor && rank === 1 ? { color: awardColor } : {}}>{coverage_score}</span>
            </td>
          ) : (
            <td className={classNames(styles.speedMeasure, styles.speedScore)}>
              <span style={awardColor && rank === 1 ? { color: awardColor } : {}}>{speed_score}{video_score}{game_score}{overall_connectivity_score}</span>
            </td>
          )}
        </tr>
      );
    });
  };

  getTooltipText = (tooltips, type, defaultString) => {
    const tooltipOverride = get(tooltips, type);

    const tooltipText = tooltipOverride ? tooltipOverride : defaultString;
    if (!tooltipText) {
      throw new Error('Error: no overrides or default text provided.');
    }
    return tooltipText;
  };

  renderTableHead = () => {
    const hasDownload = get(this.props.rankings, '[0].download_speed');
    const hasUpload = get(this.props.rankings, '[0].upload_speed');
    const hasCoverage = get(this.props.rankings, '[0].coverage_score');
    const hasTopRated = get(this.props.rankings, '[0].rating');

    const speedScoreTooltipText = this.getTooltipText(this.props.tooltips, 'speedScore', c.tooltips.speedScore);
    const videoScoreTooltipText = this.getTooltipText(this.props.tooltips, 'videoScore', c.tooltips.videoScore);
    const gameScoreTooltipText = this.getTooltipText(this.props.tooltips, 'gameScore', c.tooltips.gameScore);
    const speedtestConnectivityScoreTooltipText = this.getTooltipText(this.props.tooltips, 'speedtestConnectivityScore', c.tooltips.speedtestConnectivityScore);

    const coverageScoreTooltipText = this.getTooltipText(
      this.props.tooltips,
      'coverageScore',
      c.tooltips.coverageScore
    );
    const ratingTooltipText = this.getTooltipText(this.props.tooltips, 'rating', c.tooltips.rating);

    return (
      <thead>
        <tr>
          <th />
          {hasDownload ? (
            <th className="u-align-right">
              <Icon className={styles.colorDownload} icon="download" size="normal" /> Mbps
            </th>
          ) : null}
          {hasUpload ? (
            <th className="u-align-right">
              <Icon className={styles.colorUpload} icon="upload" size="normal" /> Mbps
            </th>
          ) : null}
          {hasTopRated ? (
            <th className="u-align-right">
              Rating{' '}
              <Flyout label={ratingTooltipText} position="right" triggers={['click']} theme="dark">
                <Icon color="primary" icon="info" size="normal" />
              </Flyout>
            </th>
          ) : null}
          {hasCoverage ? (
            <th className={styles.speedScore}>
              Coverage Score{' '}
              <Flyout label={coverageScoreTooltipText} position="right" triggers={['click']} theme="dark">
                <Icon color="primary" icon="info" size="normal" />
              </Flyout>
            </th>
          ) : null}
          {// only display the speed score column/tooltip if the data being shown are speed scores
            !hasCoverage && !hasTopRated && this.props.rankings && this.props.rankings.length > 0 && this.props.rankings[0].speed_score ? (
              <th className={styles.speedScore}>
                Speed Score{' '}
                <Flyout label={speedScoreTooltipText} position="right" triggers={['click']} theme="dark">
                  < Icon color="primary" icon="info" size="normal" />
                </Flyout>
              </th>
            ) : null}

          {// only display the overall connectivity score column/tooltip if the data being shown are speed scores
            !hasCoverage && !hasTopRated && this.props.rankings && this.props.rankings.length > 0 && this.props.rankings[0].overall_connectivity_score ? (
              <th className={styles.speedScore}>
                Speedtest Connectivity Score{' '}
                <Flyout label={speedtestConnectivityScoreTooltipText} position="right" triggers={['click']} theme="dark">
                  < Icon color="primary" icon="info" size="normal" />
                </Flyout>
              </th>
            ) : null}
          {// only display the video score column/tooltip if the data being shown are video scores
            !hasCoverage && !hasTopRated && this.props.rankings && this.props.rankings.length > 0 && this.props.rankings[0].video_score ? (
              <th className={styles.speedScore}>
                Video Score{' '}
                <Flyout label={videoScoreTooltipText} position="right" triggers={['click']} theme="dark">
                  <Icon color="primary" icon="info" size="normal" />
                </Flyout>
              </th>
            ) : null}
          {// only display the game score column/tooltip if the data being shown are video scores
            !hasCoverage && !hasTopRated && this.props.rankings?.length > 0 && this.props.rankings[0].game_score ? (
              <th className={styles.speedScore}>
                Game Score{' '}
                <Flyout label={gameScoreTooltipText} position="right" triggers={['click']} theme="dark">
                  <Icon color="primary" icon="info" size="normal" />
                </Flyout>
              </th>
            ) : null}
        </tr>
      </thead>
    );
  };

  renderExpandButton = () => {
    const label = this.state.isExpanded ? 'Show less' : 'Show more';

    return (
      <Button className={styles.expandButton} onClick={this.toggleExpand} size="small" theme="contentOnly">
        {label} <Icon color="primary" icon="caret" size="medium" rotate={this.state.isExpanded ? 180 : 0} />
      </Button>
    );
  };

  // eslint-disable-next-line complexity
  render() {
    const {
      header,
      awardPageRoute,
      companyBlurb,
      expandable,
      awardColor,
      className,
      report,
      report2,
      report3,
      rankings
    } = this.props;
    return (
      <div className={classNames(styles.award, className)}>
        <h1 className={styles.header} style={awardColor ? { color: awardColor } : {}}>
          {awardPageRoute ? (
            <Link style={awardColor ? { color: awardColor } : {}} to={awardPageRoute}>
              {header}
            </Link>
          ) : (
            header
          )}
        </h1>
        <h2 className={styles.subHeader}>
          {awardPageRoute ? (
            <Link className={styles.awardLink} to={awardPageRoute}>
              {this.props.subHeader}
            </Link>
          ) : (
            this.props.subHeader
          )}
        </h2>
        <p className={styles.companyBlurb} dangerouslySetInnerHTML={{ __html: companyBlurb }} />
        {report && report.text && report.link && (
          <p className={styles.awardReport}>
            <a href={report.link} target="_blank">
              {report.text}
            </a>
          </p>
        )}
        {report2 && report2.text && report2.link && (
          <p className={styles.awardReport}>
            <a href={report2.link} target="_blank">
              {report2.text}
            </a>
          </p>
        )}
        {report3 && report3.text && report3.link && (
          <p className={styles.awardReport}>
            <a href={report3.link} target="_blank">
              {report3.text}
            </a>
          </p>
        )}
        <table className={classNames(styles.rankings, { [styles.expanded]: this.state.isExpanded })}>
          {this.renderTableHead()}
          <tbody>{this.renderRows()}</tbody>
        </table>
        {expandable && rankings ? this.renderExpandButton() : null}
      </div>
    );
  }
}
